<script setup>
import Layout from "../Layouts/Customer.vue";
import Checkbox from "primevue/checkbox";
import Button from "primevue/button";
import {ref} from "vue";
import {useToast} from "primevue/usetoast";
import moment from "moment";
import {useForm} from "@inertiajs/inertia-vue3";
import ConsentSignature from "../Components/ConsentSignature.vue";
import {required$, true$} from "../utils/validators";
import {useVuelidate} from "@vuelidate/core";
import {scrollToFirstError} from "../utils/form";
import {dataURLtoFile} from "../utils/file-utils";
import {Inertia} from "@inertiajs/inertia";


const props = defineProps([
    'customer',
    'menuItems',
    'flash',
]);
const toast = useToast()
const consent = ref(props.customer?.cooltech_define_consent)
const consentSlug = ref('cooltech-define-consents')
const isEdit = ref(Boolean(consent.value))
const submitting = ref(false)
const consentSignature = ref(null)
const date = ref(
    isEdit.value ? moment(consent.value.updated_at) : moment()
)
const form = useForm({
    covered_areas: isEdit.value ? consent.value.covered_areas : [],
    signature_key: null,
    customer_uuid: props.customer.uuid,
    is_agree_signature: isEdit.value,
    signature_img: isEdit.value ? consent.value.signature_img : null,
    has_signature: null,
    date: date.value.format('DD.MM.YYYY'),
});

const rules = {
    form: {
        covered_areas: {
            required$,
        },
        is_agree_signature: {
            true$,
        },
        signature_key: {
            custom$: {
                $validator: function(value) {
                    return form.has_signature !== false
                },
                $message: 'Dieses Feld ist erforderlich',
            }
        }
    }
}

const v$ = useVuelidate(rules, {
    form,
})

const submit = async (e, isDownload) => {
    let signatureData = null
    const signaturePad = consentSignature.value.getSignaturePad()

    if (signaturePad) {
        const { isEmpty, data } = signaturePad.saveSignature()

        form.has_signature = ! isEmpty
        signatureData = data
    }

    v$.value.$validate()

    if (v$.value.$errors.length) {
        scrollToFirstError()
        toast.add({
            severity: "error",
            summary: "Error",
            life: 5000,
        });

        return
    }

    submitting.value = true

    if (signatureData) {
        const { key:signatureKey } = await Vapor.store(dataURLtoFile(signatureData), {
            progress: progress => {
            }
        })

        form.signature_key = signatureKey
    }

    const postUrl = isEdit.value
        ? `/${consentSlug.value}/${consent.value.uuid}`
        : `/${consentSlug.value}`

    form.post(
        postUrl,
        {
            preserveState: false,
            onError: (errors) => {
                toast.add({
                    severity: "error",
                    summary: "Error",
                    life: 5000,
                });
            },

            onSuccess: (page) => {
                toast.add({
                    severity: "success",
                    summary: "Aktualisiert",
                    life: 5000,
                });

                if (isDownload) {
                    window.location.href = `/${consentSlug.value}/${consent.value.uuid}/download`
                }
            },

            onFinish: (visit) => {
                submitting.value = false;
            }
        }
    )
}

const download = async () => {
    if (! submitting.value) {
        await submit(null, true)
    }
}
</script>
<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <div class="px-14 py-7">
            <div class="relative mb-9">
                <h1 class="text-black text-base font-bold">
                    Einverständniserklärung und Vertrag zur Kryolipolyse
                </h1>

                <div v-if="isEdit" class="absolute top-0 right-0">
                    <i class="pi pi-file-pdf text-[#C5C6CC] cursor-pointer"
                       @click="download"></i>
                </div>
            </div>

            <div class="text-sm">
                <h3 class="text-black font-bold">Einführung</h3>

                <div class="flex flex-col gap-3 mb-9">
                   <p>
                       Die Kryolipolyse mit dem Cooltech Define ist ein nicht-invasives Verfahren zur Reduktion von Fettzellen durch
                       kontrollierte Kühlung. Ziel der Behandlung ist die Verringerung von Fettdepots an spezifischen Körperstellen.
                       Der Kunde wurde umfassend über die Bedingungen, den Ablauf, die möglichen Folgen der Behandlung sowie die
                       Nachsorge informiert.
                   </p>
                </div>

                <ul class="mb-9">
                    <li>
                        <h3 class="text-sm font-bold my-5">
                            1. Vertragsgegenstand
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Der Kunde beauftragt VIGORY® mit der Durchführung von Behandlungen mittels Kryolipolyse zur
                                Reduktion von Fettdepots.
                            </p>

                            <p>
                                Umfang und Preise unserer Leistungen sind auf unserer Website ersichtlich und können unter dem
                                folgenden Link abgerufen werden [Einfügen Link/Verweis]. Es gelten die im Zeitpunkt der Buchung
                                ausgewiesenen Preise.
                            </p>

                            <p>
                                Zu behandelnde Bereiche:
                            </p>

                            <div>
                                <div class="grid grid-cols-6 gap-4">
                                    <div v-for="value in [
                                    'Bauch',
                                    'Gesäss',
                                    'Hüfte',
                                    'Oberschenkel',
                                    'Rücken',
                                    'Arme',
                                ]">
                                        <div class="flex items-center gap-4">
                                            <Checkbox
                                                :invalid="v$.form.covered_areas.$error"
                                                v-model="form.covered_areas"
                                                :value="value"
                                                @change="v$.form.covered_areas.$touch"
                                            ></Checkbox>

                                            <div>{{ value }}</div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="v$.form.covered_areas.$error" class="text-sm text-[#dc2626]">
                                    {{ v$.form.covered_areas.$errors[0].$message }}
                                </div>
                            </div>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            2. Beschreibung des Verfahrens
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Während der Kryolipolyse werden Applikatoren auf die Haut aufgesetzt, die das darunterliegende Fettgewebe
                                kühlen. Dies kann zu einer Reduktion der Fettzellen in dem behandelten Bereich führen. Die Behandlung
                                dauert in der Regel zwischen 45 und 70 Minuten pro Bereich. Normalerweise kann ein zufriedenstellendes
                                Ergebnis mit 2-3 Anwendungen erzielt werden.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            3. Risiken und Nebenwirkungen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Obwohl die Kryolipolyse als sicher gilt, können folgende Nebenwirkungen auftreten:
                                Kältegefühl während der Behandlung.
                                Vorübergehende Rötung, Hitzegefühl, Hautreizung und Schmerzen - diese Symptome können
                                während oder kurz nach der Behandlung auftreten, sind aber in der Regel gering und verschwinden innerhalb
                                weniger Minuten wieder.
                                Rötungen und Ödeme im behandelten Bereich sind zu erwarten und sollten innerhalb von 2–10 Tagen
                                ausheilen.
                                Vorübergehende oder seltene länger anhaltende Taubheit im behandelten Bereich können bis zu 8 Wochen
                                anhalten.
                                Nach der Behandlung können Bauchbeschwerden oder eine erhöhte Empfindlichkeit der Haut um den Nabel
                                auftreten, die in der Regel innerhalb einer Woche nach der Behandlung abklingen.
                                Trockene Haut – kann innerhalb von 12 Stunden nach der Behandlung auftreten und verschwindet in der Regel
                                spontan innerhalb von 48 Stunden. Das Auftragen von Feuchtigkeitscreme kann hilfreich sein.
                                Es kann zu postinflammatorischer Hyperpigmentierung kommen.
                                Bei unsachgemässer Anwendung durch unqualifiziertes Personal kann es in seltenen Einzelfällen zu
                                Hautverbrennungen kommen.
                                Eine sehr seltene, aber mögliche Nebenwirkung der Kryolipolyse ist die Paradoxe Adipose Hyperplasie (PAH).
                                PAH führt paradoxerweise zu einer Vergrösserung des Fettgewebes in dem behandelten Bereich anstelle der
                                gewünschten Reduktion.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            4. Alternativen zur Kryolipolyse
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Es gibt verschiedene Alternativen zur Kryolipolyse, einschliesslich Diät und Bewegung, sowie andere ästhetische
                                Verfahren wie Fettabsaugung oder nicht-invasive Radiofrequenz-, Ultraschall- oder Laserbehandlungen.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            5. Vorbehalte und Gesundheitsschutz
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                VIGORY behält sich das Recht vor, die Behandlung abzulehnen oder abzubrechen, insbesondere wenn
                                gesundheitliche Bedenken vorliegen.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            6. Leistungsinhalt und Haftung
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Es kann bis zu 3 Monate dauern, bis die endgültigen Ergebnisse der Behandlung sichtbar sind. In seltenen
                                Fällen kann der Erfolg gering oder ausbleibend sein.
                            </p>

                            <p>
                                Der Leistungsinhalt umfasst die fachgerechte Durchführung der Behandlung. Das Behandlungsergebnis kann
                                sowohl von der Behandlung als auch von der Nachsorge des Kunden beeinflusst werden und hängt
                                insbesondere vom individuellen Heilungsverlauf ab. VIGORY garantiert keinen spezifischen Behandlungserfolg,
                                inkl. Fettreduktion.
                            </p>

                            <p>
                                VIGORY haftet gemäss den gesetzlichen Vorschriften für Verletzungen von vertraglichen und
                                ausservertraglichen Pflichten. Schadenersatz wird bei Vorsatz und grober Fahrlässigkeit geleistet. Bei leichter
                                Fahrlässigkeit haftet VIGORY nur bei Verletzung von Leben, Körper oder Gesundheit sowie bei Verletzung
                                wesentlicher Vertragspflichten. In diesen Fällen ist die Haftung auf den vorhersehbaren, typischerweise
                                eintretenden Schaden begrenzt. VIGORY haftet nicht für Schäden, die auf Umstände zurückzuführen sind, für
                                die der Kunde verantwortlich ist.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            7. Terminverschiebungen und -absagen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Vereinbarte Folgetermine sind verbindlich. VIGORY stellt Personal und technische Ausstattung für die
                                Termine bereit. Terminverschiebungen sind kostenfrei, wenn sie mindestens 24 Stunden vor dem
                                vereinbarten Termin erfolgen.
                            </p>

                            <p>
                                Bei Absagen innerhalb von 24 Stunden vor dem Termin oder bei Nichterscheinen kann VIGORY die vereinbarte
                                Vergütung verlangen.
                            </p>

                            <p>
                                Einsparungen oder anderweitige Verwendung der Kapazitäten werden berücksichtigt.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            8. Datenschutz und Urheberrechte
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                VIGORY erhebt und bearbeitet Personendaten des Kunden nach Massgabe der Datenschutzerklärung, die
                                unter folgendem Link abgerufen werden kann: https://www.vigory.ch/datenschutz
                            </p>

                            <p>
                                Der Kunde stimmt der fotografischen Dokumentation der behandelten Bereiche und der anonymen
                                Veröffentlichung der Ergebnisse zu Werbezwecken zu.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            9. Zustimmung zur Behandlung
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Ich bestätige hiermit, dass ich die oben genannten Informationen zur Kryolipolyse gelesen und verstanden
                                habe. Mir ist bewusst, dass jeder Mensch individuell reagiert und gewisse Nebeneffekte auftreten können
                                und, dass der Behandlungserfolg nur dann erzielt werden kann, wenn das definierte Therapiekonzept zu Ende
                                geführt wird. Ich kenne die Risiken und möglichen Nebenwirkungen und willige freiwillig in die Durchführung
                                der Behandlungen ein. Ich hatte die Gelegenheit, Fragen zu stellen und diese wurden zu meiner Zufriedenheit
                                beantwortet.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            10. Haftungsausschluss
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Ich verstehe, dass das Ergebnis der Behandlung variieren kann und keine spezifischen Resultate garantiert
                                werden können. Ich entbinde hiermit die behandelnde Fachkraft, sowie das Center von jeglicher Haftung,
                                soweit gesetzlich zulässig
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            11. Schlussbestimmungen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Die Angaben im Anamnesefragebogen habe ich wahrheitsgemäss beantwortet.
                                Wir bitten Sie, uns umgehend über jegliche Änderungen Ihres Gesundheitszustandes zu informieren. Dies
                                umfasst, ist aber nicht beschränkt auf, neu diagnostizierte Erkrankungen, Änderungen bestehender
                                Krankheiten, die Einnahme neuer Medikamente oder Änderungen in der Medikation. Diese
                                Informationen sind entscheidend, um Ihre Behandlung optimal anzupassen. Bitte beachten Sie, dass wir
                                keinerlei Verantwortung für gesundheitliche Folgen übernehmen können, die auf nicht mitgeteilte
                                Änderungen zurückzuführen sind. Vielen Dank für Ihr Verständnis und Ihre Kooperation.
                            </p>
                        </div>
                    </li>
                </ul>

                <div class="mb-9">
                    <ConsentSignature
                        ref="consentSignature"
                        :customer="customer"
                        :form="form"
                        :v$="v$"
                        :is-edit="isEdit"
                    />
                </div>

                <div class="flex justify-end">
                    <Button
                        @click="submit"
                        :loading="submitting"
                        label="speichern"
                        class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                    />
                </div>
            </div>
        </div>
    </Layout>
</template>
